export type ObjectWithChildren<T> = T & { children: ObjectWithChildren<T>[] };

/**
 * Converts an array to a new tree-like array, objects are getting a new "children"-attribute
 * @param objects the array that of wich a tree-like copy should be created
 * @param parentAttribute the attribute name in wich the parent key is stored
 * @param identifier the attribute name that stores the identifier of an object
 * @returns a new tree-like array with new objects
 */
export function arrayToTree<T>(
  objects: T[],
  getParentKey: (object: T) => any,
  getIdentifier: (object: T) => any
) {
  const map = new Map();
  const roots: Array<ObjectWithChildren<T>> = [];

  objects.forEach((obj) => {
    map.set(getIdentifier(obj), obj);
  });

  objects.forEach((obj) => {
    const parentKey = getParentKey(obj);
    const parent = parentKey ? map.get(parentKey) : undefined;

    if (parent) {
      parent.children?.push(obj) ?? (parent.children = [obj]);
    } else {
      roots.push(map.get(getIdentifier(obj)));
    }
  });

  return roots;
}

// EXAMPLE
// const arr = [
//   {
//     someAttributes: "",
//     weirdId: "hEHIec",
//     weirdParent: "12345",
//   },
//   {
//     someAttributes: "",
//     weirdId: "12345",
//     weirdParent: undefined,
//   },
// ];
// const newHierarchyArray = arrayToHierarchy(arr, obj => obj.weirdParent, obj => obj.weirdId);

/**
 * Converts any hierarchy-array to a flat array. Does not alter the array
 * @param objects the hierarchy array
 * @param getChildren a getter function to get the attribute where the children are stored
 * @returns a flat array containing all objects of the hierarchy array
 */
export function treeToArray<T>(
  objects: Array<T>,
  getChildren: (obj: T) => Array<T>
) {
  const flattedObjects: Array<T> = [];

  objects.forEach((obj) => {
    flattedObjects.push(obj);
    const children = getChildren(obj);
    if (children && children.length > 0) {
      const nextChildren = treeToArray(children, getChildren);
      flattedObjects.push(...nextChildren);
    }
  });

  return flattedObjects;
}

// EXAMPLE (revert upper example)
// const flatArray = treeToArray(newHierarchyArray, (obj) => obj.children)
